import { FC, ReactNode } from 'react';
import { FormProvider, type UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type EditablePhraseType, PHRASE_TYPES } from '@/entities/conversations';
import { readFormInterval } from './recordForm';
import { TranscriptListenButton } from './TranscriptListenButton';
import { TranscriptPhraseFields } from './TranscriptPhraseFields';
import { TranscriptPhrasePickField } from './TranscriptPhrasePickField';
import { TranscriptTimeFields } from './TranscriptTimeFields';
import type { TranscriptRecordFormValues } from './types';

type Props = {
  form: UseFormReturn<TranscriptRecordFormValues>;
  type: EditablePhraseType;
  /** Заголовок формы: что правится или добавляется */
  title: ReactNode;
  /** Сохранение по Enter в поле формы */
  onSubmit: () => void;
  /** Отрезок этой формы выделяется на шкале записи */
  isTimelineTarget?: boolean;
  /** Вынос выделенного фрагмента текста в новую реплику — только у правки реплики */
  onSplit?: (split: { fragment: string; rest: string }) => void;
  /** Фрагмент уже вынесен и правится рядом: повторный вынос недоступен */
  isSplitOpen?: boolean;
  /** Подвал формы — её «Отменить» и «Сохранить»; у пары реплик при выносе он общий и стоит под обеими */
  children?: ReactNode;
};

/**
 * Поля записи расшифровки её вида и прослушивание отрезка по введённым границам.
 */
export const TranscriptRecordForm: FC<Props> = ({
  form,
  type,
  title,
  onSubmit,
  isTimelineTarget = true,
  onSplit,
  isSplitOpen = false,
  children,
}) => {
  const { t } = useTranslation(['dialogs']);
  const [startedAt, finishedAt] = useWatch({ control: form.control, name: ['startedAt', 'finishedAt'] });

  return (
    <FormProvider {...form}>
      <form
        className="@container flex flex-col gap-3"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1 text-sm font-medium">{title}</div>
          <TranscriptListenButton type={type} interval={readFormInterval(startedAt, finishedAt)} />
        </div>
        {type === PHRASE_TYPES.PHRASE && <TranscriptPhraseFields onSplit={onSplit} isSplitOpen={isSplitOpen} />}
        {type === PHRASE_TYPES.INTERRUPTION && (
          <div className="grid grid-cols-1 @md:grid-cols-2 gap-3">
            <TranscriptPhrasePickField
              name="interruptedPhraseId"
              label={t('dialogs:transcriptEdit.interruptedPhrase')}
            />
            <TranscriptPhrasePickField
              name="interruptionPhraseId"
              label={t('dialogs:transcriptEdit.interruptionPhrase')}
            />
          </div>
        )}
        <TranscriptTimeFields isTimelineTarget={isTimelineTarget} />
        {children}
      </form>
    </FormProvider>
  );
};
